import { ICarWashObject } from "../types/carWash";

// ---------------------------------------------------------------------------
// Washing class types — canonical location (moved from settings.service.ts)
// ---------------------------------------------------------------------------
export type VehicleType = "sedan" | "cuv" | "suv" | "minivan" | "van";
export type ServiceType = "inner" | "outer" | "both";
export type WashClass = "econom" | "standard" | "premium";

export interface ICarWashClassEntry {
  vehicleType: VehicleType;
  serviceType: ServiceType;
  washClass: WashClass | null;
}

export interface ICarWashClassCombo {
  vehicleType: VehicleType;
  serviceType: ServiceType;
  econom_min: number;
  econom_max: number;
  standard_min: number;
  standard_max: number;
  /** Lower bound for premium tier. Premium has no upper bound — any price >= premium_min is premium. */
  premium_min: number;
}

export interface IWashingClassSettings {
  key: "washing_class_config";
  combinations: ICarWashClassCombo[];
}

export interface IUpdateWashingClassSettingsPayload {
  combinations: ICarWashClassCombo[];
}

export interface IBulkRecomputeResult {
  matched: number;
  modified: number;
  errors: number;
}

/**
 * VehicleType (phase 21-01) — mirrors the backend Mongoose model
 * `ukar-backend/src/models/vehicleTypeModel.js`. `key` is stored lowercase and
 * used as the reference string in CarBrand.models[].vehicleType.
 */
interface IVehicleTypeEntry {
  _id?: string;
  key: string;
  label: string;
  order: number;
}

enum EUserType {
  Owner = "Owner",
  Washer = "Washer",
  Administrator = "Administrator",
  User = "User",
}

enum EBusinessType {
  Citizen = "Citizen",
  Private = "Private",
  Legal = "Legal",
}

interface IUserObject {
  _id: string;
  userType: EUserType;
  firstName: string;
  lastName: string;
  country_code: string;
  phone: string;
  isBlocked?: boolean;
  availabilityStatus?: "available" | "busy" | "offline";
  // Phase 30 — dispatch/availability fields (backend 30-01). Additive, optional.
  availableAtCarWash?: string | null; // CarWash ID the employee is currently available at
  currentLocation?: { type: "Point"; coordinates: number[] }; // GeoJSON [lon, lat]
  locationUpdatedAt?: string;
}

interface IOwnerObject extends IUserObject {
  gender?: string;
  dateOfBirth?: string;
  email?: string;
  businessType?: EBusinessType;
  voen?: string;
  contact?: {
    fullName: string;
    country_code: string;
    phone: string;
  };
  carWashes: ICarWashObject[];
  carWashCount?: number;
  employees: IUserObject[];
  balance: number;
  relationshipManager?: {
    _id: string;
    username: string;
    // Backend nested-populates relationshipManager.role as a StaffRole object
    // ({ _id, name, label, permissions }), not a string. Optional-safe in case
    // the field is absent on legacy/unpopulated documents.
    role?: {
      _id: string;
      name: string;
      label?: string;
      permissions?: string[];
    };
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    country_code?: string;
  };
  registrationStatus?: "admin_created" | "pending_approval" | "approved" | "rejected";
  // Phase 26 — RM pending-owner rotation (additive, optional). Backend annotates these.
  lastSeenAt?: string;
  pendingAssignedAt?: string;
  pendingStartedAt?: string;
  hasPendingReview?: boolean;
  // pendingReviewCount — every open ChangeReview under this owner, across all
  // request types (backend adminController getOwners / getMyOwners). Additive,
  // optional. Distinct from hasPendingReview, which stays a boolean.
  pendingReviewCount?: number;
  // Phase 38 — owner contract PDF (additive, optional). Backend annotates these.
  // `contractFileKey` is deliberately NOT exposed: the raw S3 key never leaves
  // the backend — access is only ever via a short-lived presigned downloadUrl.
  contractFileName?: string | null;
  contractUploadedAt?: string | null;
  contractUploadedBy?: string | null;
  isOnline?: boolean;
  onlineEmployeeCount?: number;
  createdAt: string;
  updatedAt: string;
}

// Assign RM picker (260801-sa1) — minimal shape for GET /admins/rms. No `role`
// object needed here: the endpoint already pre-filters to relationship_manager only.
export interface IEligibleRM {
  _id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  country_code?: string;
}

// Phase 27 — admin-scoped employee CRUD request payloads.
// Create requires all five fields; userType is restricted to the two employee roles.
interface IEmployeeRequest {
  firstName: string;
  lastName: string;
  phone: string;
  country_code: string;
  userType: "Washer" | "Administrator";
}

// Edit only updates name/type (phone is immutable on the backend PATCH — D-07).
// The backend whitelist never accepted `active` and does not accept `isBlocked`;
// block state is changed only via the dedicated block/unblock endpoints.
interface IEmployeeUpdateRequest {
  firstName?: string;
  lastName?: string;
  userType?: "Washer" | "Administrator";
}

// Phase 27 — Owner statistics summary (GET /admins/owners/:id/statistics).
interface IOwnerStatistics {
  total: number;
  completed: number;
  cancelled: number;
  missedExpired: number;
  missedDeclined: number;
  missed: number;
}

type OwnerStatisticsRange = "7d" | "30d" | "90d" | "12m";
type OwnerStatisticsGranularity = "day" | "month";

// One bucket of the owner statistics series. `date` is "YYYY-MM-DD" (day) or
// "YYYY-MM" (month), Asia/Baku local.
interface IOwnerStatisticsBucket {
  date: string;
  total: number;
  completed: number;
  cancelled: number;
  missedExpired: number;
  missedDeclined: number;
}

// GET /admins/owners/:id/statistics/series?range=
interface IOwnerStatisticsSeries {
  range: OwnerStatisticsRange;
  granularity: OwnerStatisticsGranularity;
  timezone: string;
  series: IOwnerStatisticsBucket[];
}

// Phase 38 — owner contract read payload (GET /admins/owners/:id/contract).
// `downloadUrl` is a short-lived presigned DigitalOcean URL, minted per request:
// treat it as a bearer credential — never persist or log it.
// All four fields are null when the owner has no contract yet (200, not 404).
export interface IOwnerContract {
  downloadUrl: string | null;
  contractFileName: string | null;
  contractUploadedAt: string | null;
  contractUploadedBy: string | null;
}

// Phase 38 — owner contract upload response (POST /admins/owners/:id/contract).
export interface IOwnerContractUploadRes {
  contractFileName: string;
  contractUploadedAt: string;
  contractUploadedBy: string;
}

interface ICreateOwnerRes {
  status: string;
  data: IOwnerObject;
  message?: string;
}

interface IGetOwnersRes {
  status: string;
  results: number;
  data: IOwnerObject[];
}

interface IGetOwnerRes {
  status: string;
  data: IOwnerObject;
}

// Phase 26 — RM-scoped owner endpoints use the ApiResponse.success envelope
// `{ status, message, data }`, so the owners array lives at response.data.data.
interface IMyOwnersRes {
  status: string;
  message: string;
  data: IOwnerObject[];
}

interface ICarWash {
  _id: string;
  legalName: string;
  address: string;
  phone: string;
  rating: number;
  status: string;
  images: string[];
  vehicleTypes?: string[];
  washClasses?: ICarWashClassEntry[];
}

interface IGetOwnerCarWashesRes {
  status: string;
  results: number;
  data: ICarWashObject[];
}

interface OwnerResponse {
  status: string;
  data: IOwnerObject;
}

interface INotification {
  _id: string;
  type:
    | "new_pending_owner"
    | "carwash_change_request"
    | "rm_assignment"
    | "sla_owner_reminder"
    | "sla_owner_escalation"
    | "sla_changereview_escalation";
  title: string;
  body: string;
  isRead: boolean;
  refId?: string;
  refModel?: "User" | "CarWash";
  createdAt: string;
}

export type {
  IUserObject,
  IOwnerObject,
  IEmployeeRequest,
  IEmployeeUpdateRequest,
  IOwnerStatistics,
  OwnerStatisticsRange,
  OwnerStatisticsGranularity,
  IOwnerStatisticsBucket,
  IOwnerStatisticsSeries,
  ICreateOwnerRes,
  IGetOwnersRes,
  IGetOwnerRes,
  IMyOwnersRes,
  ICarWash,
  IGetOwnerCarWashesRes,
  OwnerResponse,
  IVehicleTypeEntry,
  INotification,
};

export { EUserType, EBusinessType };

// The ChangeReview types mirror ukar-backend/src/models/changeReviewModel.js but
// predate this file and are defined in ../types/review, which several screens
// already import directly. Re-exported here so the cross-project rule ("the
// backend model's mirror lives in api.types.ts") resolves to something real
// rather than to a file that silently lacks them. The definitions stay in one
// place; only the entry point is added.
export type { IChangeReview, IFieldDiff, IGetChangeReviewsRes } from "../types/review";
export { isFieldDiff } from "../types/review";

// IWashRequest (and its review sub-types) mirror ukar-backend/src/models/washModel.js.
// This file used to carry its own drifted duplicate (no tags/partnerReview/cancellation
// fields); the real, kept-in-sync definition now lives in ../types/wash.types, so this
// re-export is the single entry point rule 6 expects here.
export type { IWashRequest, IWashReview, IPartnerReview } from "../types/wash.types";

// Mirrors ukar-backend/src/models/reasonModel.js (admin-managed cancel reasons and review tags).
export type TReasonListType = "customerCancel" | "partnerCancel" | "reviewTagOwner" | "reviewTagUser";
export interface IReasonLabels { en: string; ru?: string; az?: string; ge?: string; }
export interface IReason { _id: string; listType: TReasonListType; value: string; labels: IReasonLabels; order: number; active: boolean; createdAt?: string; updatedAt?: string; }
